<template>
  <v-container id="documents_root" fluid>
    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col>
        <span class="text-h5">
          {{ $t('navigationMenu.recycleBinDocuments') }}
        </span>
      </v-col>
    </v-row>
    <v-row class="mb-6 px-6" align="center" justify="start">
      <span class="subtitle-1 mr-4">{{ $t('documents.content.filters') }}</span>
      <v-chip-group
        :color="setSignatureChainStatusFilterChipColor()"
        class="filterButtonChip"
        mandatory
        @change="filterSignatureChainStatus"
      >
        <v-chip
          v-for="signatureChainStatus in signatureChainStatuses"
          :key="signatureChainStatus.value"
          class="elevation-1"
          color="white"
          label
          :value="signatureChainStatus.value"
          filter
          filter-icon="mdi-close-circle-outline"
        >
          {{ signatureChainStatus.text }}
        </v-chip>
      </v-chip-group>
      <v-row>
        <v-col>
          <v-text-field
            id="search_field"
            :label="$t('dataTable.header.signatureChains.fileName')"
            single-line
            hide-details
            clearable
            @input="filterSignatureChainOrigDocFileName"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-autocomplete
            id="participant_email_search_field"
            :search-input.sync="searchMember"
            :items="membertList"
            :label="$t('dataTable.header.signatureChains.memberId')"
            :placeholder="$t('autocomplete.startTyping')"
            :no-data-text="$t('dataTable.content.noDataAvailable')"
            item-text="email"
            item-value="id"
            clearable
            @input="filterSignatureChainMemberId"
          ></v-autocomplete>
        </v-col>
        <v-col>
          <v-autocomplete
            id="creator_email_search_field"
            :search-input.sync="searchCreator"
            :items="creatorList"
            :label="$t('dataTable.header.signatureChains.creatorId')"
            :placeholder="$t('autocomplete.startTyping')"
            :no-data-text="$t('dataTable.content.noDataAvailable')"
            item-text="email"
            item-value="id"
            clearable
            @input="filterSignatureChainCreatedBy"
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-row>
    <v-card class="mt-4">
      <v-data-table
        id="documents_table"
        must-sort
        :headers="headers"
        :items="signatureChainList"
        :options.sync="signatureChainDataTablePagination"
        :server-items-length="signatureChainDataTableTotalItems"
        :loading="signatureChainDataTableLoading"
        :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :no-data-text="$t('dataTable.content.noDataAvailable')"
        :no-results-text="$t('dataTable.content.noMatchingRecordsFound')"
        :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10,25,50],
        }"
      >
        <template #[`item.origDocument.fileName`]="{ item }">
          <a v-if="item.origDocument" class="black--text text--primary" @click="openInNewTab(item,$event)">
            <long-text :text="item.origDocument.fileName" :max-char="60" />
          </a>
          <span v-else>  <long-text :text="item.batch.name" :max-char="60" /></span>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="setSignatureChainStatusChipColorInDataTable(item.status)" label>
            {{ setSignatureChaniStatusToFriendlyNameInDataTable(item.status) }}
          </v-chip>
        </template>
        <template #[`item.createdAt`]="{ item }">
          <span class="font-italic caption">{{ convertDate(item.createdAt) }}</span>
        </template>
        <template #[`item.updatedAt`]="{ item }">
          <span class="font-italic caption">{{ convertDate(item.updatedAt) }}</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn class="pa-1 " text @click="showSignatureChainParticipants(item)">
            <v-icon>mdi-account-multiple</v-icon>
            {{ $t('signatureChains.participants.title') }}
          </v-btn>
          <v-btn class="pa-1 " text @click="downloadDocument(item,$event)">
            <v-icon>mdi-tray-arrow-down</v-icon>
            {{ $t('dataTable.content.action.download') }}
          </v-btn>
          <v-btn class="pa-1 delete-btn " text @click="deleteSignatureChain(item.id, $event)">
            <v-icon>mdi-delete</v-icon>
            {{ $t('dataTable.content.action.delete') }}
          </v-btn>
          <v-menu offset-x left>
            <template #[`activator`]="{ on }">
              <v-btn class="pa-1 " icon v-on="on" @click="setSignatureChainForInformationMenu(item)">
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-text>
                <v-list>
                  <v-list-item-subtitle>{{ $t('dataTable.header.documents.id') }} : {{ selectedSignatureChainData.id }}</v-list-item-subtitle>
                  <v-list-item-subtitle>{{ $t('dataTable.header.documents.createdAt') }} : {{ convertDate(selectedSignatureChainData.createdAt) }}</v-list-item-subtitle>
                  <!-- <v-list-item-subtitle>{{ $t('dataTable.header.documents.fileSize') }} : {{ selectedSignatureChainData.origDocument.fileSize }} {{ $t('dataTable.header.documents.fileSizeInByte') }}</v-list-item-subtitle> -->
                </v-list>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- PARTICIPANTS DIALOG -->
    <v-dialog v-model="showSignatureChainParticipantsDialog" max-width="745" class="rounded-xl">
      <signature-chain-participants
        :signature-chain="selectedSignatureChainData"
        @close="showSignatureChainParticipantsDialog = false"
      />
    </v-dialog>
    <download-mode-menu
      :pos-x="downloadMenuX" :pos-y="downloadMenuY" :visible="downloadMenuVisible"
      :new-tab="downloadModalNewTab"
    >
    </download-mode-menu>
  </v-container>
</template>

<script>
import FileSaver from 'file-saver'
import debounce from 'lodash.debounce'
import LongText from '../../../components/LongText'
import eventBus, { showConfirmationMenu, showToastError, showToastSuccess } from '../../../event-bus'
import documents from '../../../services/api/documents'
import signatureChains from '../../../services/api/signatureChains'
import users from '../../../services/api/users'
import DownloadModeMenu from './DownloadModeMenu'
import SignatureChainParticipants from './SignatureChainParticipants'
import SignatureChainStatus from './SignatureChainStatus'
import documentDownloadMode from './documentDownloadMode'

export default {
  name: 'RecycleBinSignatureChains',

  components: {
    LongText,
    SignatureChainParticipants,
    DownloadModeMenu
  },

  data() {
    return {
      filters: {
        signatureChainStatus: null,
        signatureChainOrigDocFileName: null,
        signatureChainMemberId: null,
        signatureChainCreatedBy: null
      },
      signatureChainDataTablePagination: {
        // sortBy: ['origDocument.fileName']
        sortBy: ['updatedAt'],
        sortDesc: [true]
      },
      signatureChainList: [],
      signatureChainDataTableTotalItems: 0,
      signatureChainDataTableLoading: false,
      showSignatureChainParticipantsDialog: false,
      selectedSignatureChainData: {
        id: 1,
        createdAt: '2023-04-27T09:59:18.972Z',
        origDocument: {
          fileSize: 42653
        },
        signatures: []
      },
      signatureChainStatuses: [
        {
          value: null,
          text: this.$t('signatureChains.states.all')
        },
        {
          value: SignatureChainStatus.DONE,
          text: this.$t('signatureChains.states.completed')
        },
        {
          value: SignatureChainStatus.FAILED,
          text: this.$t('signatureChains.states.failedTypes.failed')
        }
      ],
      searchMember: null,
      membertList: [],
      searchCreator: null,
      creatorList: [],
      headers: [
        {
          text: this.$t('dataTable.header.signatureChains.fileName'),
          align: 'left',
          value: 'origDocument.fileName'
        },
        {
          text: this.$t('dataTable.header.signatureChains.status'),
          value: 'status',
          sortable: false,
          align: 'center'
        },
        {
          text: this.$t('dataTable.header.signatureChains.createdAt'),
          align: 'center',
          value: 'createdAt'
        },
        {
          text: this.$t('dataTable.header.signatureChains.updatedAt'),
          align: 'center',
          value: 'updatedAt'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      // download mode menu
      downloadMenuVisible: false,
      downloadMenuX: 0,
      downloadMenuY: 0,
      downloadModalNewTab: false
    }
  },

  watch: {
    filters() {
      this.signatureChainDataTableLoading = true
      this.searchSignatureChainsWatcher()
    },

    signatureChainDataTablePagination() {
      this.searchSignatureChainsWatcher()
    },

    searchMember() {
      this.searchMemberWatcher()
    },

    searchCreator() {
      this.searchCreatorWatcher()
    }
  },
  mounted() {
    this.searchCreatorUsers()
    this.searchMemberUsers()
  },
  methods: {
    async searchSignatureChains() {
      const q = {
        recycleBin: true, // must be fixed
        status: this.filters.signatureChainStatus == null ? null : this.filters.signatureChainStatus,
        fileName: this.filters.signatureChainOrigDocFileName == null ? null : this.filters.signatureChainOrigDocFileName,
        createdBy: this.filters.signatureChainCreatedBy == null ? null : this.filters.signatureChainCreatedBy,
        memberId: this.filters.signatureChainMemberId == null ? null : this.filters.signatureChainMemberId
      }

      const pageRequest = {
        'page-num': this.signatureChainDataTablePagination.page,
        'page-size': this.signatureChainDataTablePagination.itemsPerPage,
        'sort-field': this.signatureChainDataTablePagination.sortBy[0],
        'sort-order': this.signatureChainDataTablePagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = await signatureChains.listSignatureChains(q, pageRequest)
        this.signatureChainList = page.content
        this.signatureChainDataTableTotalItems = page.total
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.signatureChainDataTableLoading = false
      }
    },

    searchSignatureChainsWatcher: debounce(function() {
      this.searchSignatureChains()
    }, 500),

    async searchMemberUsers() {
      try {
        const page = await users.listUsers(this.searchMember, null)
        this.membertList = page.content
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },

    searchMemberWatcher: debounce(function() {
      this.searchMemberUsers()
    }, 500),

    async searchCreatorUsers() {
      try {
        const page = await users.listUsers(this.searchCreator, null)
        this.creatorList = page.content
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },

    searchCreatorWatcher: debounce(function() {
      this.searchCreatorUsers()
    }, 500),

    filterSignatureChainStatus(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainStatus: val })
    },

    filterSignatureChainOrigDocFileName(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainOrigDocFileName: val })
    },

    filterSignatureChainCreatedBy(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainCreatedBy: val })
    },

    filterSignatureChainMemberId(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainMemberId: val })
    },

    async openInNewTab(chain, event) {
      let mode = documentDownloadMode.ORIGINAL
      if (chain.signingTrailMode === documentDownloadMode.DETACHED) {
        this.downloadModalNewTab = true
        mode = await this.showDownloadModal(event)

        if (mode === 'CANCEL') {
          return
        }
      }
      const docId = chain.signedDocument?.id ??
        chain.signatures.findLast(x => x.signedDocument)?.signedDocument.id ??
        chain.origDocument.id

      try {
        const blob = await documents.downloadDocument(docId, mode)

        const url = URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }

      this.downloadMenuVisible = false
    },

    showSignatureChainParticipants(signatureChain) {
      this.selectedSignatureChainData = signatureChain
      this.showSignatureChainParticipantsDialog = true
    },

    async downloadDocument(item, event) {
      // getting the latest document

      const chain = item

      let mode = documentDownloadMode.ORIGINAL
      if (chain.signingTrailMode === documentDownloadMode.DETACHED) {
        mode = await this.showDownloadModal(event)
        if (mode === 'CANCEL') return
      }

      try {
        if (!chain.origDocument && chain.batch) {
          await this.handleBatchDownload(chain, mode)
          return
        }

        const { docId, fileName } = this.getDocIdAndFileName(chain)
        const documentData = await documents.downloadDocument(docId, mode)
        const downloadFileName = this.getDownloadFileName(fileName, mode)

        await FileSaver.saveAs(documentData, downloadFileName)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },
    async handleBatchDownload(chain, mode) {
      let docId = chain.batch.id
      const fileName = chain.batch.name

      if (chain.signatures.every(x => x.status === 'CREATED')) {
        try {
          const batch = await documents.getBatch(chain.batch.id)

          batch.documents.forEach(async doc => {
            const documentData = await documents.downloadDocument(doc.id, mode)
            await FileSaver.saveAs(
              documentData.slice(0, documentData.size, 'application/pdf'),
              doc.fileName
            )
          })
          return
        } catch (error) {
          console.error(error)
        }
      } else {
        // ASIC
        const signatures = chain.signatures.filter(signature => signature.signedAt)
        signatures.sort((a, b) => new Date(b.signedAt) - new Date(a.signedAt))
        docId = signatures[0].signedDocument.id

        try {
          const documentData = await documents.downloadDocument(docId, mode)

          await FileSaver.saveAs(documentData, chain.signedDocument.fileName)
        } catch (error) {
          showToastError(this.getErrorMessage(error))
        }
      }
    },
    getDownloadFileName(fileName, mode) {
      const lastDotIndex = fileName.lastIndexOf('.')

      const hasExtension = lastDotIndex !== -1
      const nameWithoutExt = hasExtension ? fileName.substring(0, lastDotIndex) : fileName
      const extension = hasExtension ? fileName.substring(lastDotIndex) : ''

      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() === '.pdf') {
        return fileName
      }
      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() !== '.pdf') {
        return fileName + '.pdf'
      }

      if (mode === documentDownloadMode.FULL) {
        return nameWithoutExt + '.zip'
      }

      return nameWithoutExt + '-signingtrail' + (hasExtension ? extension : '.pdf')
    },
    getDocIdAndFileName(chain) {
      if (chain.signedDocument) {
        return {
          docId: chain.signedDocument.id,
          fileName: chain.signedDocument.fileName.replace(/\.[^/.]+$/, '')
        }
      }

      const lastSignedDoc = chain.signatures.findLast(x => x.signedDocument)
      return {
        docId: lastSignedDoc?.signedDocument?.id || chain.origDocument.id,
        fileName: chain.origDocument?.fileName || 'Document'
      }
    },

    async downloadBatch(chain, mode) {
      const batch = await documents.getBatch(chain.batch.id)
      for (const doc of batch.documents) {
        const documentData = await documents.downloadDocument(doc.id, mode)
        await FileSaver.saveAs(
          documentData.slice(0, documentData.size, 'application/pdf'),
          doc.fileName
        )
      }
    },
    async deleteSignatureChain(signatureChainId, e) {
      const conf = await showConfirmationMenu({ title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await signatureChains.deleteSignatureChain(signatureChainId)
        this.searchSignatureChains()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },

    setSignatureChainForInformationMenu(signatureChain) {
      this.selectedSignatureChainData = signatureChain
    },

    setSignatureChainStatusFilterChipColor() {
      if (this.isSignatureChainDone(this.filters.signatureChainStatus)) {
        return 'success signed'
      } else if (this.isSignatureChainFailed(this.filters.signatureChainStatus)) {
        return 'error'
      } else {
        return 'filterbuttonTextColor'
      }
    },

    setSignatureChainStatusChipColorInDataTable(signatureChainStatus) {
      if (this.isSignatureChainDone(signatureChainStatus)) {
        return 'signed'
      } else if (this.isSignatureChainFailed(signatureChainStatus)) {
        return 'error failed lighten-3 error--text text--darken-2'
      } else {
        return null
      }
    },

    setSignatureChaniStatusToFriendlyNameInDataTable(signatureChainStatus) {
      if (this.isSignatureChainDone(signatureChainStatus)) {
        return this.$t('signatureChains.states.completed')
      } else if (this.isSignatureChainFailed(signatureChainStatus)) {
        return this.$t('signatureChains.states.failedTypes.failed')
      } else {
        return signatureChainStatus
      }
    },

    isSignatureChainDone(signatureChainStatus) {
      return signatureChainStatus === SignatureChainStatus.DONE
    },

    isSignatureChainFailed(signatureChainStatus) {
      return signatureChainStatus === SignatureChainStatus.FAILED
    },
    async showDownloadModal(event) {
      this.downloadMenuVisible = false // Ensure the menu is closed before showing
      this.$nextTick(() => {
        this.downloadMenuVisible = true
      })
      this.downloadMenuX = event.clientX
      this.downloadMenuY = event.clientY

      const answer = await new Promise(resolve => {
        eventBus.$once('downloadModeSelected', data => resolve(data))
      })
      this.downloadModalNewTab = false

      return answer
    }
  }
}
</script>

<style>
</style>
